import { nanoid } from 'nanoid';
import type { ReadonlyDeep } from 'type-fest';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { getMarqueeCardImageUrl } from '#src/app/pages/homepage/shared/utils';

import { HomepageData, type HomepageDataQuery, type HomepageDataQueryVariables } from './api.graphql';

export type HomepagePageData = ReadonlyDeep<{
	totalUserCount: HomepageDataQuery['totalUserCount'];
	topImpactStories: HomepageDataQuery['topImpactStories'];
	petitionMarqueeItems: HomepageDataQuery['petitionMarqueeItems'];
	/**
	 * The middle-end returns an empty list when the homepage_petition_marquee FCM is disabled,
	 * so a non-empty list stands in for the FCM's isEnabled flag. Items the marquee cannot
	 * render (no image) are already filtered out, so a true here guarantees visible cards -
	 * otherwise the hero could pick the marquee over its impact-stories fallback and render
	 * an empty slot.
	 */
	shouldShowPetitionMarquee: boolean;
	aiSapId: string;
}>;

export async function getHomepagePageData(
	utilityContext: UtilityContext,
	{ locale: _locale }: { locale: string },
): Promise<HomepagePageData> {
	const {
		gql: { fetch },
	} = utilityContext;
	const { data } = await fetch<HomepageDataQuery, HomepageDataQueryVariables>({
		query: HomepageData,
		variables: {},
		rejectOnError: true,
	});

	// marquee cards need an image, and getMarqueeCardImageUrl is the same selection
	// usePetitionMarquee applies when it renders them
	const petitionMarqueeItems = data.petitionMarqueeItems.filter((item) => getMarqueeCardImageUrl(item));

	return {
		totalUserCount: data.totalUserCount,
		topImpactStories: data.topImpactStories,
		petitionMarqueeItems,
		shouldShowPetitionMarquee: petitionMarqueeItems.length > 0,
		aiSapId: nanoid(),
	};
}
